/** @owner shared — auth/permisos. RBAC permissions store with persist. */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Definir los permisos con sus claves
export const PERMISSIONS = {
  canViewHome: "Home.Ver",
  
  canEnterClinicalHistory: "HistoriasClínicas.Entrar",
  canLoadClinicalHistory: "HistoriasClínicas.Cargar",
  canViewClinicalHistory: "HistoriasClínicas.Ver",
  canEditClinicalHistory: "HistoriasClínicas.Editar",

  canViewMPI: "MPI.Ver",
  canEditMPI: "MPI.Editar",
  
  canViewProfessionals: "Profesionales.Ver",
  canEditProfessionals: "Profesionales.Editar",
  
  canViewDataAnalysis: "AnálisisDeDatos.Ver",
  
  canViewPharmacy: "Farmacia.Ver",
  canEditPharmacy: "Farmacia.Editar",
} as const;

interface PermissionsState {
  permissions: string[];

  // Actions
  setPermissions: (permissions: string[]) => void;
  clearPermissions: () => void;

  // Computed getters
  hasPermission: (permission: string) => boolean;
  getPermissionFlags: () => Record<keyof typeof PERMISSIONS, boolean>;
};

export const usePermissionsStore = create<PermissionsState>()(
  persist(
    (set, get) => ({
      permissions: [],

      setPermissions: (permissions) => set({ permissions }),

      clearPermissions: () => set({ permissions: [] }),

      hasPermission: (permission) => {
        const state = get();
        return state.permissions.includes(permission);
      },

      getPermissionFlags: () => {
        const state = get();
        return Object.entries(PERMISSIONS).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: state.permissions.includes(value),
          }),
          {} as Record<keyof typeof PERMISSIONS, boolean>
        );
      },
    }),
    {
      name: 'permissions-storage',
      partialize: (state) => ({
        permissions: state.permissions,
      }),
    }
  )
);

// Selectores útiles
export const usePermissions = () =>
  usePermissionsStore((state) => state.permissions);

export const usePermissionFlags = () =>
  usePermissionsStore((state) => state.getPermissionFlags());

export const useHasPermission = (permission: string) =>
  usePermissionsStore((state) => state.hasPermission(permission));

// Hook para reemplazar useHandlePermissions
export const useHandlePermissions = () => {
  const setPermissions = usePermissionsStore((state) => state.setPermissions);
  const permissions = usePermissionsStore((state) => state.permissions);
  const getPermissionFlags = usePermissionsStore((state) => state.getPermissionFlags);
  const clearPermissions = usePermissionsStore((state) => state.clearPermissions);
  const handlePermissions = async (newPermissions: string[]) => {
    setPermissions(newPermissions);
  };

  return {
    handlePermissions,
    clearPermissions,
    permissions,
    permissionsFlags: getPermissionFlags(),
  };
};
